import { Controller } from "@hotwired/stimulus"

/**
 * Searchable Select Controller
 *
 * Transforms a native <select> into a filterable dropdown.
 * Falls back gracefully — the original select still holds the value.
 *
 * Usage:
 *   <div data-controller="searchable-select">
 *     <select data-searchable-select-target="select" name="contact_id">
 *       <option value="">Choose...</option>
 *       <option value="1">Alice</option>
 *       <option value="2">Bob</option>
 *     </select>
 *   </div>
 */
export default class extends Controller {
  static targets = ["select"]

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.buildUI()
    this.selectTarget.style.display = "none"
  }

  disconnect() {
    if (this.wrapper) {
      this.wrapper.remove()
    }
    this.selectTarget.style.display = ""
  }

  buildUI() {
    this.options = Array.from(this.selectTarget.options).map(opt => ({
      value: opt.value,
      label: opt.textContent,
      selected: opt.selected
    }))

    this.wrapper = document.createElement("div")
    this.wrapper.className = "searchable-select"

    // Trigger button
    this.trigger = document.createElement("button")
    this.trigger.type = "button"
    this.trigger.className = "searchable-select-trigger"
    this.trigger.setAttribute("tabindex", "0")
    this.updateTriggerLabel()

    const chevron = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    chevron.setAttribute("class", "h-4 w-4 text-mod-warm-500 flex-shrink-0")
    chevron.setAttribute("fill", "none")
    chevron.setAttribute("stroke", "currentColor")
    chevron.setAttribute("viewBox", "0 0 24 24")
    chevron.setAttribute("stroke-width", "2")
    chevron.innerHTML = '<polyline points="6 9 12 15 18 9"/>'
    this.trigger.appendChild(chevron)

    this.trigger.addEventListener("click", (e) => {
      e.preventDefault()
      this.toggle()
    })

    // Dropdown
    this.dropdown = document.createElement("div")
    this.dropdown.className = "searchable-select-dropdown"
    this.dropdown.style.display = "none"

    // Search input
    const searchWrap = document.createElement("div")
    searchWrap.className = "searchable-select-search"
    this.searchInput = document.createElement("input")
    this.searchInput.type = "text"
    this.searchInput.placeholder = "Search..."
    this.searchInput.addEventListener("input", () => this.filter())
    this.searchInput.addEventListener("keydown", (e) => this.handleSearchKeydown(e))
    searchWrap.appendChild(this.searchInput)
    this.dropdown.appendChild(searchWrap)

    // Options list
    this.optionsList = document.createElement("div")
    this.optionsList.className = "searchable-select-options"
    this.dropdown.appendChild(this.optionsList)

    this.renderOptions()

    this.wrapper.appendChild(this.trigger)
    this.wrapper.appendChild(this.dropdown)

    this.selectTarget.insertAdjacentElement("afterend", this.wrapper)

    // Close on outside click
    this.outsideClickHandler = (e) => {
      if (!this.wrapper.contains(e.target)) this.close()
    }
    document.addEventListener("click", this.outsideClickHandler)
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.isOpen = true
    this.dropdown.style.display = ""
    this.wrapper.classList.add("searchable-select--open")
    this.searchInput.value = ""
    this.filter()
    requestAnimationFrame(() => this.searchInput.focus())
  }

  close() {
    this.isOpen = false
    this.dropdown.style.display = "none"
    this.wrapper.classList.remove("searchable-select--open")
    this.highlightedIndex = -1
  }

  filter() {
    const query = this.searchInput.value.toLowerCase().trim()
    this.filteredOptions = query
      ? this.options.filter(o => o.label.toLowerCase().includes(query))
      : this.options
    this.highlightedIndex = -1
    this.renderOptions()
  }

  renderOptions() {
    const items = this.filteredOptions || this.options
    if (items.length === 0) {
      this.optionsList.innerHTML = '<div class="searchable-select-empty">No results found</div>'
      return
    }

    this.optionsList.innerHTML = ""
    items.forEach((opt, idx) => {
      const el = document.createElement("div")
      el.className = "searchable-select-option"
      if (opt.value === this.selectTarget.value) {
        el.classList.add("searchable-select-option--selected")
      }
      if (idx === this.highlightedIndex) {
        el.classList.add("searchable-select-option--highlighted")
      }
      el.textContent = opt.label
      el.addEventListener("click", () => this.selectOption(opt))
      el.addEventListener("mouseenter", () => {
        this.highlightedIndex = idx
        this.updateHighlight()
      })
      this.optionsList.appendChild(el)
    })
  }

  selectOption(opt) {
    this.selectTarget.value = opt.value
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.updateTriggerLabel()
    this.close()
  }

  updateTriggerLabel() {
    const selected = this.selectTarget.options[this.selectTarget.selectedIndex]
    const label = selected ? selected.textContent : "Select..."
    // Preserve the chevron
    const chevron = this.trigger?.querySelector("svg")
    if (this.trigger) {
      this.trigger.textContent = ""
      const span = document.createElement("span")
      span.className = "truncate"
      span.textContent = label
      this.trigger.appendChild(span)
      if (chevron) this.trigger.appendChild(chevron)
    }
  }

  handleSearchKeydown(event) {
    const items = this.filteredOptions || this.options

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.highlightedIndex = Math.min(this.highlightedIndex + 1, items.length - 1)
      this.updateHighlight()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
      this.updateHighlight()
    } else if (event.key === "Enter") {
      event.preventDefault()
      if (this.highlightedIndex >= 0 && items[this.highlightedIndex]) {
        this.selectOption(items[this.highlightedIndex])
      }
    } else if (event.key === "Escape") {
      this.close()
    }
  }

  updateHighlight() {
    const children = this.optionsList.querySelectorAll(".searchable-select-option")
    children.forEach((el, idx) => {
      el.classList.toggle("searchable-select-option--highlighted", idx === this.highlightedIndex)
    })
    // Scroll into view
    if (children[this.highlightedIndex]) {
      children[this.highlightedIndex].scrollIntoView({ block: "nearest" })
    }
  }
}
